import dayjs from 'dayjs';

import {
    APP_ACCOUNT_STATUS_LABEL,
    ICenterMember,
    MemberAppAccountFields,
    getMemberAppAccountStatus,
} from '@/features/publicMember/types';

type AppAccountStatusBadgeProps = {
    member?: (MemberAppAccountFields & Pick<ICenterMember, 'onboardingPending' | 'onboardingStale'>) | null;
    showWithdrawnAt?: boolean;
    showOnboardingStatus?: boolean;
};

const STATUS_STYLE = {
    linked: {
        backgroundColor: '#EFF6FF',
        color: '#1D4ED8',
        borderColor: '#BFDBFE',
    },
    withdrawn: {
        backgroundColor: '#FFF1F2',
        color: '#BE123C',
        borderColor: '#FECDD3',
    },
    unlinked: {
        backgroundColor: '#F8FAFC',
        color: '#64748B',
        borderColor: '#E2E8F0',
    },
    stale: {
        backgroundColor: '#FFFBEB',
        color: '#B45309',
        borderColor: '#FDE68A',
    },
} as const;

const AppAccountStatusBadge = ({
    member,
    showWithdrawnAt = false,
    showOnboardingStatus = false,
}: AppAccountStatusBadgeProps) => {
    const status = getMemberAppAccountStatus(member);
    const showOnboarding = showOnboardingStatus && status !== 'withdrawn';
    const onboardingLabel = showOnboarding
        ? member?.onboardingStale ? '가입 미완료' : member?.onboardingPending ? '가입 진행 중' : ''
        : '';
    const statusStyle = onboardingLabel
        ? member?.onboardingStale ? STATUS_STYLE.stale : STATUS_STYLE.unlinked
        : STATUS_STYLE[status];
    const withdrawnDate =
        status === 'withdrawn' && member?.appWithdrawnAt
            ? dayjs(member.appWithdrawnAt).format('YYYY.MM.DD')
            : '';
    const label = onboardingLabel || (status === 'linked' ? '앱 연동' : APP_ACCOUNT_STATUS_LABEL[status]);
    const title = onboardingLabel
        ? member?.onboardingStale
            ? '앱 가입 후 30일 넘게 정보 입력을 하지 않았어요'
            : '회원이 앱에서 정보 입력 전이에요'
        : withdrawnDate ? `앱 탈퇴일 ${withdrawnDate}` : undefined;

    return (
        <span
            title={title}
            role={title ? 'note' : undefined}
            tabIndex={title ? 0 : undefined}
            aria-label={title ? `${label}: ${title}` : undefined}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                padding: '5px 9px',
                borderRadius: 999,
                border: `1px solid ${statusStyle.borderColor}`,
                backgroundColor: statusStyle.backgroundColor,
                color: statusStyle.color,
                fontSize: 12,
                fontWeight: 800,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
            }}
        >
            {label}
            {showWithdrawnAt && withdrawnDate && (
                <span
                    style={{
                        paddingLeft: 5,
                        borderLeft: `1px solid ${statusStyle.borderColor}`,
                        fontWeight: 700,
                        opacity: 0.85,
                    }}
                >
                    {withdrawnDate}
                </span>
            )}
        </span>
    );
};

export default AppAccountStatusBadge;
